<template>
  <div>
    <drawer @toggleSidebar="toggleSidebar" :sidebar="sidebar" />
    <Header image="logo.png" @toggleSidebar="toggleSidebar" />

    <div class="traffic-container">
      <div class="container">
        <div class="traffic-wrapper" v-loading="loading">
          <!-- 文章内容 -->
          <div v-if="articleData" class="article-content">
            <div class="article-body" v-html="articleData"></div>
          </div>
          
          <!-- 加载中 -->
          <div v-else-if="loading" class="loading-placeholder">
            <el-skeleton :rows="10" animated />
          </div>
          
          <!-- 空状态 -->
          <el-empty v-else description="暂无内容" />
        </div>
      </div>
    </div>

    <footer-one />
    <div class="go-top-area">
      <div class="go-top-wrap">
        <div class="go-top-btn-wrap">
          <a href="#" class="go-top go-top-btn d-block">
            <i class="fa fa-angle-double-up"></i>
            <i class="fa fa-angle-double-up"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Drawer from "@/components/Mobile/Drawer.vue";
import FooterOne from "@/components/Utility/Footer/FooterOne.vue";
import Header from "@/components/Utility/Header/index";
import { getArticleRichText } from "@/api/article";
import { eventBus } from '@/utils/eventBus.js';
import config from "@/config";

export default {
  name: "Traffic",
  components: { Header, FooterOne, Drawer },
  data() {
    return {
      sidebar: false,
      loading: true,
      articleData: null,
      currentLocale: localStorage.getItem('locale') || 'zh-cn',
    };
  },
  computed: {
    // 根据当前语言环境获取对应的文章ID
    articleId() {
      const isEn = this.currentLocale === 'en' || this.currentLocale === 'en-US';
      return isEn ? config.ENG_TRAFFICID : config.TRAFFICID;
    }
  },
  created() {
    document.addEventListener("scroll", this.topToBottom);
    
    // 监听语言切换事件
    eventBus.$on('language-changed', this.handleLanguageChange);
    
    // 监听 i18n 语言变化（如果有）
    if (this.$i18n && this.$i18n.on) {
      this.$i18n.on('languageChanged', this.handleLanguageChange);
    }
    
    this.fetchArticle();
  },
  beforeDestroy() {
    document.removeEventListener("scroll", this.topToBottom);
    eventBus.$off('language-changed', this.handleLanguageChange);
    if (this.$i18n && this.$i18n.off) {
      this.$i18n.off('languageChanged', this.handleLanguageChange);
    }
  },
  methods: {
    toggleSidebar() {
      this.sidebar = !this.sidebar;
    },
    topToBottom() {
      const result = document.querySelector(".go-top");
      if (
        document.body.scrollTop > window.innerHeight ||
        document.documentElement.scrollTop > window.innerHeight
      ) {
        result?.classList.add("active");
      } else {
        result?.classList.remove("active");
      }
    },
    /**
     * 处理语言切换
     */
    handleLanguageChange(lang) {
      this.currentLocale = lang || 'zh-cn';
      // 重新获取文章
      this.fetchArticle();
    },
    /**
     * 获取文章详情
     * 根据当前语言环境自动选择对应的文章ID
     */
    async fetchArticle() {
      const id = this.articleId;
      if (!id) {
        console.warn('文章ID未配置');
        this.loading = false;
        return;
      }
      
      this.loading = true;
      try {
        const response = await getArticleRichText(id);
        if (response.code < 300 && response.data) {
          this.articleData = response.data.body;
        } else {
          this.$message.error(response.msg || '获取文章失败');
        }
      } catch (error) {
        console.error('获取文章失败:', error);
        this.$message.error('获取文章失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },
    /**
     * 格式化日期
     */
    formatDate(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
  },
};
</script>

<style scoped>
.traffic-container {
  padding: 40px 0 60px;
  min-height: 60vh;
  background: #f8fafc;
}

.traffic-wrapper {
  max-width: 900px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  padding: 40px 50px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.article-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a3a6b;
  margin-bottom: 16px;
  line-height: 1.4;
}

.article-meta {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-bottom: 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f2f5;
  color: #999;
  font-size: 14px;
}

.article-body {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.article-body :deep(p) {
  margin-bottom: 16px;
}

.article-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 16px 0;
}

.article-body :deep(h2),
.article-body :deep(h3) {
  margin: 24px 0 12px;
  color: #1a3a6b;
}

.article-body :deep(ul),
.article-body :deep(ol) {
  padding-left: 24px;
  margin-bottom: 16px;
}

.article-body :deep(blockquote) {
  border-left: 4px solid #2978dd;
  padding-left: 16px;
  margin: 16px 0;
  color: #666;
  background: #f5f8fe;
  padding: 12px 20px;
  border-radius: 4px;
}

.loading-placeholder {
  padding: 20px 0;
}

@media (max-width: 768px) {
  .traffic-container {
    padding: 20px 16px;
  }

  .traffic-wrapper {
    padding: 24px 20px;
  }

  .article-title {
    font-size: 22px;
  }

  .article-body {
    font-size: 15px;
  }

  .article-meta {
    flex-wrap: wrap;
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .traffic-wrapper {
    padding: 16px;
  }

  .article-title {
    font-size: 19px;
  }

  .article-body {
    font-size: 14px;
  }
}
</style>